/**
 * YouTube links, as people actually paste them.
 *
 * An item carries an optional `video_url` typed in by staff, and what lands
 * there is whatever a phone's Share button produced: `youtu.be/<id>?si=…`,
 * `youtube.com/shorts/<id>?si=…`, a full `watch?v=…&list=…`, sometimes just the
 * id. Every client needs the bare id — an embed URL, a poster image — so the
 * parsing happens ONCE, server-side, and clients are handed something they can
 * template without knowing any of this.
 */

/** A YouTube id is exactly 11 URL-safe base64 characters. */
const ID_RE = /^[\w-]{11}$/;

/** Hosts that serve YouTube watch/embed pages. */
const HOSTS = new Set(['youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtube-nocookie.com']);

/** Path prefixes that carry the id as the NEXT segment. */
const PATH_KINDS = new Set(['embed', 'shorts', 'live', 'v']);

/**
 * The video id inside a YouTube link, or null when the text is not one.
 *
 * Returning null for anything unrecognized is the point: a client is then never
 * handed a value it would render as a broken player.
 */
export function youTubeId(input: string | null | undefined): string | null {
  const raw = (input ?? '').trim();
  if (!raw) return null;
  if (ID_RE.test(raw)) return raw; // someone pasted the bare id

  let url: URL;
  try {
    // Tolerate a link typed without a scheme ("youtu.be/x"), which is what a
    // copy from an address bar often gives.
    url = new URL(/^[a-z][\w+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();
  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0] ?? '';
    return ID_RE.test(id) ? id : null;
  }
  if (!HOSTS.has(host)) return null;

  const v = url.searchParams.get('v');
  if (v && ID_RE.test(v)) return v;

  const [kind, id] = url.pathname.split('/').filter(Boolean);
  if (kind && id && PATH_KINDS.has(kind.toLowerCase()) && ID_RE.test(id)) return id;
  return null;
}

/** True when this text would actually play — what the item form warns about. */
export function isYouTubeUrl(input: string | null | undefined): boolean {
  return youTubeId(input) !== null;
}

/**
 * Player URL for an `<iframe>`/WebView. `youtube-nocookie.com` so a shopper who
 * never presses play is not tracked, and `playsinline` so a phone plays the
 * video in place instead of taking over the screen.
 */
export function youTubeEmbedUrl(id: string, opts: { autoplay?: boolean } = {}): string {
  const params = new URLSearchParams({ rel: '0', playsinline: '1', modestbranding: '1' });
  if (opts.autoplay) params.set('autoplay', '1');
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

/** Poster frame — used as the tap-to-play thumbnail so no player loads unasked. */
export function youTubeThumbUrl(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}
